import { Box, Divider, Flex, Form, MatrixPicker, Radio, Select, Text } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { domain } from 'modules/data';
import { memo, ReactNode } from 'react';

import { schedulerFormNames } from '../../Scheduler.constants';
import { monthNumbers, monthsItems } from './SchedulerMonth.constants';
import { messages } from './SchedulerMonth.messages';
import { schedulerRadioCheckedState } from './SchedulerMonth.states';

export const SchedulerMonth = memo(() => {
    const [schedulerRadioChecked, setSchedulerRadioChecked] = useAtom(schedulerRadioCheckedState);

    return (
        <Form.Part>
            <Radio.Group
                direction='column'
                spacing='16'
                value={schedulerRadioChecked}
                onChange={(value: domain.ScheduleMonthType) => setSchedulerRadioChecked(value)}
                name={schedulerFormNames.monthOption}
            >
                <Radio value={domain.ScheduleMonthType.EveryMonth} size='small'>
                    {messages.everyMonth}
                </Radio>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleMonthType.EveryMonthsStartingIn} size='small'>
                        {messages.every}

                        {schedulerRadioChecked !== domain.ScheduleMonthType.EveryMonthsStartingIn && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.everyMonthsStartingIn({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleMonthType.EveryMonthsStartingIn && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select name={schedulerFormNames.monthPeriod} size='small' items={monthNumbers} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.startingMonth}
                            </Text>

                            <Box width={96}>
                                <Select name={schedulerFormNames.startMonth} size='small' items={monthsItems} />
                            </Box>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex spacing='8' direction='column'>
                    <Radio value={domain.ScheduleMonthType.SpecificMonth} size='small'>
                        {messages.specificMonth}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleMonthType.SpecificMonth && (
                        <Box width={224 + 24} spacing='0 0 0 24'>
                            <MatrixPicker name={schedulerFormNames.specificMonth} maxColumns={3} items={monthsItems} />
                        </Box>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleMonthType.EveryMonthBetween} size='small'>
                        {messages.monthBetween}

                        {schedulerRadioChecked !== domain.ScheduleMonthType.EveryMonthBetween && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.everyMonthBetween({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleMonthType.EveryMonthBetween && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={96}>
                                <Select size='small' items={monthsItems} name={schedulerFormNames.monthBetweenStart} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.and}
                            </Text>

                            <Box width={96}>
                                <Select size='small' items={monthsItems} name={schedulerFormNames.monthBetweenEnd} />
                            </Box>
                        </Flex>
                    )}
                </Flex>
            </Radio.Group>
        </Form.Part>
    );
});

SchedulerMonth.displayName = 'SchedulerMonth';
